import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '@/_utils/axios';
import { LoadingStatuses } from '@/constants/constants';
import { ActiveVersion, isActiveVersionAVersion, TitleMetadata, TitleVersionsResponse } from '@/redux/slices/slice_types';
import { TitleChildrenResponseV2, TitleResponseV2, TitleSliceV2State, TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { parseFns } from '@/redux/selectors/configSelectors';
import { getJob } from '@/redux/slices/jobSlice';
import { saveTitleToHistory } from './titleSlice';
import { RootState } from '../store';

const initialState: TitleSliceV2State = {
  titlesById: {},
  titleLoadingStatusById: {},
  childrenByTitleId: {},
  childrenLoadingStatusByTitleId: {},
  rootTitleId: null,
  activeTitleId: null,
  activeTitleLevel: null,
  activeTitleHierarchy: [],
  selectedTitleId: null,
  activeVersion: null,
  activeVersions: [],
  activeVersionsByTitleId: {},
  activeVersionsLoadingStatusByTitleId: {},
  titleMetadata: {} as TitleMetadata,
  titleMetadataLoading: null
};

export const fetchTitleV2 = createAsyncThunk<TitleResponseV2['data']['data'], number, { state: RootState }>(
  'titleV2/fetchTitle',
  async (id, { rejectWithValue, signal, dispatch, getState }) => {
    const hierarchy = getState().orderPlacement.selectedHierarchy;

    try {
      const titleRes = await axios.get<TitleResponseV2>(`/inventory/titles/${id}/`, { signal, baseURL: '/api/v2/' });
      const titleData = titleRes.data.data;
      const hasHierarchy = !!titleData.metadata_languages?.some((lang) => typeof lang === 'object' && lang !== null && lang.hierarchy === hierarchy);

      dispatch(
        saveTitleToHistory({
          id: titleData.id,
          name: titleData.name,
          ...(hasHierarchy && { hierarchy })
        })
      );

      return titleData;
    } catch (error: any) {
      return rejectWithValue(error.data);
    }
  }
);

export const fetchTitleChildrenV2 = createAsyncThunk('titleV2/fetchTitleChildren', async (id: number, { rejectWithValue, signal }) => {
  return axios
    .post<TitleChildrenResponseV2>(
      `/inventory/titles/${id}/children/?page_size=999`,
      {
        order_on: 'sequence_order',
        direction: 'asc',
        thumbnails: true
      },
      { signal, baseURL: '/api/v2/' }
    )
    .then((titleRes) => ({
      titleId: id,
      children: titleRes.data.data
        .filter((child) => child.entity_type !== 'version')
        .sort((a, b) => {
          if (a.sequence_order === b.sequence_order) {
            return a.name < b.name ? -1 : 1;
          }
          return 0;
        })
    }))
    .catch((error) => rejectWithValue(error.data));
});

export const fetchTitleChildrenAndVersionsV2 = createAsyncThunk(
  'titleV2/fetchTitleChildrenAndVersionsV2',
  async (id: number, { rejectWithValue, signal }) => {
    return axios
      .post<TitleChildrenResponseV2>(
        `/inventory/titles/${id}/children/?page_size=999`,
        {
          order_on: 'sequence_order',
          direction: 'asc',
          thumbnails: true
        },
        { signal, baseURL: '/api/v2/' }
      )
      .then((titleRes) => ({
        titleId: id,
        children: titleRes.data.data.sort((a, b) => {
          return a.sequence_order === 0 ? 1 : b.sequence_order === 0 ? -1 : a.sequence_order - b.sequence_order;
        })
      }))
      .catch((error) => rejectWithValue(error.data));
  }
);

export const getTitleVersions = createAsyncThunk('titleV2/getTitleVersions', async (titleId: number, { rejectWithValue, getState }) => {
  const parseVersionsFn = parseFns.orderPlacementConfig?.parseVersionsFn;
  const state = getState() as RootState;
  const titleSearchValue = state.orderPlacement.searchValue;

  return axios
    .post<TitleVersionsResponse>(`/inventory/titles/${titleId}/versions/?page_size=999`)
    .then(async (versionRes) => {
      if (versionRes.data?.pagination?.total > 999) {
        const versionRes2 = await axios.post(`/inventory/titles/${titleId}/versions/?page_size=999&page_num=1`);
        versionRes.data.data = [...versionRes.data.data, ...versionRes2.data.data];
      }
      // let versions = versionRes.data?.data?.filter((version) => version.status === 'active' && version.parent === titleId) || [];
      let versions = versionRes.data?.data?.filter((version) => version.status === 'active') || [];
      versions = parseVersionsFn ? parseVersionsFn(versions, { titleSearchValue }) : versions;
      return { titleId, versions };
    })
    .catch((error) => rejectWithValue(error.data));
});

export const fetchTitleMetadata = createAsyncThunk<
  { metadata: TitleMetadata },
  { body: { language: string; hierarchy: string; entity_ids: number[] } }
>('titleV2/fetchTitleMetadata', async ({ body }, { rejectWithValue, signal }) => {
  const parseTitleMetadataFn = parseFns.orderPlacementConfig?.parseTitleMetadataFn;

  try {
    const response = await axios.post(`/inventory/titles/metadata/search/`, body, { signal });
    return {
      metadata: parseTitleMetadataFn ? parseTitleMetadataFn(response.data.data, body.entity_ids) : response.data.data
    };
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const titleSliceV2 = createSlice({
  name: 'titleV2',
  initialState,
  reducers: {
    /**
     * Completely reset slice's state. Used when user leaves the Title Detail page.
     */
    clearTitleSlice: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * Remove loaded titles and versions to force a client to fetch fresh data.
     */
    clearCache: (state) => {
      state.titlesById = initialState.titlesById;
      state.titleLoadingStatusById = initialState.titleLoadingStatusById;
      state.childrenByTitleId = initialState.childrenByTitleId;
      state.childrenLoadingStatusByTitleId = initialState.childrenLoadingStatusByTitleId;
      state.activeVersion = initialState.activeVersion;
      state.activeVersions = initialState.activeVersions;
      state.activeVersionsByTitleId = initialState.activeVersionsByTitleId;
      state.activeVersionsLoadingStatusByTitleId = initialState.activeVersionsLoadingStatusByTitleId;
      state.titleMetadata = initialState.titleMetadata;
      state.titleMetadataLoading = initialState.titleMetadataLoading;
      state.selectedTitleId = initialState.selectedTitleId;
      state.activeTitleId = initialState.activeTitleId;
      state.activeTitleLevel = initialState.activeTitleLevel;
      state.activeTitleHierarchy = initialState.activeTitleHierarchy;
    },
    setRootTitleId: (state, { payload }: { payload: number }) => {
      state.rootTitleId = payload;
    },
    setActiveTitleId: (state, { payload }: { payload: number | null }) => {
      state.activeTitleId = payload;
    },
    setActiveTitleLevel: (state, { payload }: { payload: number | null }) => {
      state.activeTitleLevel = payload;
    },
    updateActiveTitleHierarchy: (state, { payload }: { payload: { id: number; level: number } }) => {
      if (state.activeTitleHierarchy.length === 0) {
        state.activeTitleHierarchy = [state.rootTitleId as number, payload.id];
        return;
      }

      if (payload.level === state.activeTitleHierarchy.length) {
        state.activeTitleHierarchy[payload.level - 1] = payload.id;
      } else if (payload.level > state.activeTitleHierarchy.length) {
        state.activeTitleHierarchy.push(payload.id);
      } else {
        state.activeTitleHierarchy = state.activeTitleHierarchy.slice(0, payload.level - 1);
        state.activeTitleHierarchy.push(payload.id);
      }
    },
    setSelectedTitleId: (state, { payload }: { payload: number | null }) => {
      state.selectedTitleId = payload;
    },
    setActiveVersion: (state, { payload }: { payload: ActiveVersion }) => {
      state.activeVersion = payload;
    },
    setTitleById: (state, { payload: { id, title } }: { payload: { id: number; title: TitleEntityV2 } }) => {
      state.titlesById[id] = title;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTitleV2.pending, (state, action) => {
      const titleId = action.meta.arg;
      state.titleLoadingStatusById[titleId] = LoadingStatuses.pending;
    });
    builder.addCase(fetchTitleV2.rejected, (state, action) => {
      const titleId = action.meta.arg;
      state.titleLoadingStatusById[titleId] = LoadingStatuses.rejected;
    });
    builder.addCase(fetchTitleV2.fulfilled, (state, { payload }) => {
      const title = payload;
      const parseDataFn = parseFns.titleConfig?.parseDataFn;
      state.titlesById[title.id] = parseDataFn ? parseDataFn(title) : title;
      state.titleLoadingStatusById[title.id] = LoadingStatuses.fulfilled;
    });
    builder.addCase(fetchTitleChildrenV2.pending, (state, action) => {
      const titleId = action.meta.arg;
      state.childrenLoadingStatusByTitleId[titleId] = LoadingStatuses.pending;
    });
    builder.addCase(fetchTitleChildrenV2.rejected, (state, action) => {
      const titleId = action.meta.arg;
      state.childrenLoadingStatusByTitleId[titleId] = LoadingStatuses.rejected;
    });
    builder.addCase(fetchTitleChildrenV2.fulfilled, (state, { payload }) => {
      const { titleId, children } = payload;
      const parseDataFn = parseFns.titleConfig?.parseDataFn;
      state.childrenLoadingStatusByTitleId[titleId] = LoadingStatuses.fulfilled;
      const parsedChildren = parseDataFn ? children.map(parseDataFn) : children;
      parsedChildren.forEach((child) => {
        state.titlesById[child.id] = child;
      });
      state.childrenByTitleId[titleId] = parsedChildren;
    });
    builder.addCase(fetchTitleChildrenAndVersionsV2.pending, (state, action) => {
      const titleId = action.meta.arg;
      state.childrenLoadingStatusByTitleId[titleId] = LoadingStatuses.pending;
    });
    builder.addCase(fetchTitleChildrenAndVersionsV2.rejected, (state, action) => {
      const titleId = action.meta.arg;
      state.childrenLoadingStatusByTitleId[titleId] = LoadingStatuses.rejected;
    });
    builder.addCase(fetchTitleChildrenAndVersionsV2.fulfilled, (state, { payload }) => {
      const { titleId, children } = payload;
      const parseDataFn = parseFns.titleConfig?.parseDataFn;
      state.childrenLoadingStatusByTitleId[titleId] = LoadingStatuses.fulfilled;
      const parsedChildren = parseDataFn ? children.map(parseDataFn) : children;
      state.childrenByTitleId[titleId] = parsedChildren;
    });
    builder.addCase(getTitleVersions.pending, (state, action) => {
      const titleId = action.meta.arg;
      state.activeVersionsLoadingStatusByTitleId[titleId] = LoadingStatuses.pending;
    });
    builder.addCase(getTitleVersions.rejected, (state, action) => {
      const titleId = action.meta.arg;
      state.activeVersionsLoadingStatusByTitleId[titleId] = LoadingStatuses.rejected;
    });
    builder.addCase(getTitleVersions.fulfilled, (state, { payload: { titleId, versions } }) => {
      state.activeVersionsLoadingStatusByTitleId[titleId] = LoadingStatuses.fulfilled;
      state.activeVersions = [...state.activeVersions, ...versions];

      versions.forEach((version) => {
        const versionParent = version.parent as number;
        if (!state.activeVersionsByTitleId[versionParent]) {
          state.activeVersionsByTitleId[versionParent] = [];
        }
        state.activeVersionsByTitleId[versionParent].push(version);
      });

      if (isActiveVersionAVersion(state.activeVersion)) {
        const existingActiveVersionId = state.activeVersion?.id;
        if (existingActiveVersionId) {
          // Update `activeVersion` with fresh data
          state.activeVersion = versions.find((v) => v.id === existingActiveVersionId) ?? null;
        }
      }
    });
    builder.addCase(fetchTitleMetadata.pending, (state) => {
      state.titleMetadataLoading = LoadingStatuses.pending;
    });
    builder.addCase(fetchTitleMetadata.rejected, (state) => {
      state.titleMetadataLoading = LoadingStatuses.rejected;
    });
    builder.addCase(fetchTitleMetadata.fulfilled, (state, { payload }: { payload: { metadata: TitleMetadata } }) => {
      const { metadata } = payload;

      if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
        state.titleMetadata = { ...state.titleMetadata, ...metadata };
      } else {
        state.titleMetadata = metadata || {};
      }

      state.titleMetadataLoading = LoadingStatuses.fulfilled;
    });
    // TODO: Title slice should not rely on the `getJob` thunk. The correct way to do it:
    //  whatever component calls `getJob`, when server returns the response, should dispatch setActiveVersion action.
    //  This piece of code was copied from the original title slice.
    builder.addCase(getJob.fulfilled, (state, { payload: { data } }) => {
      state.activeVersion = data.version;
    });
  }
});

export const {
  clearTitleSlice,
  clearCache,
  setRootTitleId,
  setActiveTitleId,
  setSelectedTitleId,
  setActiveTitleLevel,
  updateActiveTitleHierarchy,
  setActiveVersion,
  setTitleById
} = titleSliceV2.actions;

export default titleSliceV2.reducer;
